import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CardComponent } from '@components/card/card.component';
import { ConsultationOrderSummaryComponent } from '@components/consultation-request/consultation-request-payment-info/consultation-order-summary/consultation-order-summary.component';
import { ConfigService, NgxPaymentFormComponent, Payment, PaymentTypes } from '@medology/ngx-payment';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { PaymentService } from '@services/payment.service';

@Component({
  selector: 'app-consultation-request-payment-info',
  templateUrl: './consultation-request-payment-info.component.html',
  styleUrls: ['./consultation-request-payment-info.component.scss'],
  imports: [CardComponent, ConsultationOrderSummaryComponent, NgxPaymentFormComponent],
})
export class ConsultationRequestPaymentInfoComponent implements OnInit {
  @Input() public isFreeSelected: boolean = false;

  @Output() public paymentsSelected = new EventEmitter<Payment[]>();
  @Output() public formValid = new EventEmitter<void>();

  @ViewChild('ngxPaymentForm', { static: false }) public ngxPaymentFormComponent: NgxPaymentFormComponent;

  public consultationRequestService = inject(ConsultationRequestService);

  public consultationRequestPaymentForm: FormGroup;
  public selectedPaymentType: PaymentTypes = PaymentTypes.CreditCard;
  public payments: Payment[] = [];

  private configService = inject(ConfigService);
  private paymentService = inject(PaymentService);

  /**
   * Configures recharge as the active payment type if the consultation order has recharge data.
   */
  public ngOnInit(): void {
    this.consultationRequestPaymentForm = <FormGroup>this.consultationRequestService.consultationForm.get('payment');
    this.paymentService.setGiftCardConfig(this.configService);

    const recharge = this.consultationRequestService.consultationOrderDetail?.recharge;

    if (recharge) {
      this.configureRechargePayment(`Recharge: ${recharge.detail.display_name}`);
    }
  }

  /**
   * Validates the current payment form by delegating to the NgxPaymentFormComponent.
   *
   * @returns {boolean} whether the payment form is valid
   */
  public validatePaymentForm(): boolean {
    return this.ngxPaymentFormComponent?.validateForm() ?? false;
  }

  /**
   * Emits the selected payments.
   *
   * @param {Payment[]} payments the array of Payment objects that were selected
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.paymentsSelected.emit(payments);
  }

  /**
   * Sets the active payment type to free when the order summary requests it.
   */
  public onFullDiscountApplied(): void {
    this.selectedPaymentType = PaymentTypes.Free;
  }

  /**
   * Enables Recharge in the payment config and sets it as active once it becomes selectable.
   *
   * @param {string} displayName the display name for the recharge payment option
   */
  private configureRechargePayment(displayName: string): void {
    this.paymentService.enableRecharge(this.configService, displayName);
    this.selectedPaymentType = PaymentTypes.Recharge;
  }
}
